<script setup lang="ts">
import {inject, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
    getInvitations as getInvitationsFromApi,
    acceptInvitation as acceptInvitationAPI,
} from "@/Services/Admin/admin-invitations";
import {Column, DataTable, Tag, Button} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import type dayjs from "dayjs";
import {AdminIndex} from "@/Types/index.d";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";

type InvitationLocal = any;

const currentRoute = useRoute();
const router = useRouter();
const $date = inject("date") as typeof dayjs;
const invitations = ref<AdminIndex<InvitationLocal>>();
const confirm = useConfirm();
const toast = useToast();

const getInvitations = async () =>
    getInvitationsFromApi({
        ...currentRoute.query,
        include: ["for", "user"],
    }).then((data) => (invitations.value = data));
getInvitations();

const acceptInvitation = async (event, invitation: InvitationLocal) => {
    console.log(event.target);
    confirm.require({
        message: `Are you sure you want to accept the invitation for ${invitation.name}?`,
        header: "Accept Invitation",
        target: event.target,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes",
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectLabel: "No",
        rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        accept: async () => {
            try {
                const resp = await acceptInvitationAPI(invitation.id);
                console.log(resp);
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: `Invitation accepted successfully`,
                    life: 3000,
                });
                await getInvitations();
                router.push({
                    name: "admin.users.show",
                    params: {user: resp.user},
                });
            } catch (error) {
                console.error(error);
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to accept invitation",
                    life: 3000,
                });
            }
        },
    });
};
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Invitations">
                <!-- No create button needed for invitations -->
            </PageHeading>
        </div>
        <DataTable :data="invitations" dataKey="id" :loading="false" @filter="getInvitations" defaultSort="-created_at">
            <Column expander style="width: 0" />
            <Column field="id" header="ID">
                <template #body="{data}">
                    <!--                    <RouterLink-->
                    <!--                        :to="{-->
                    <!--                            name: 'admin.invitations.show',-->
                    <!--                            params: {invitation: data.id},-->
                    <!--                        }"-->
                    <!--                        class="hover:underline"-->
                    <!--                    >-->
                    <!--                    </RouterLink>-->
                    {{ data.id }}
                </template>
            </Column>
            <Column field="name" header="Name" :sortable="true">
                <template #body="{data}">
                    <!--                    <RouterLink-->
                    <!--                        :to="{-->
                    <!--                            name: 'admin.invitations.show',-->
                    <!--                            params: {invitation: data.id},-->
                    <!--                        }"-->
                    <!--                        class="hover:underline"-->
                    <!--                    >-->
                    <!--                    </RouterLink>-->
                    {{ data.name }}
                </template>
            </Column>
            <Column field="email" header="Email" :sortable="true">
                <template #body="{data}">
                    <!--                    <RouterLink-->
                    <!--                        :to="{-->
                    <!--                            name: 'admin.invitations.show',-->
                    <!--                            params: {invitation: data.id},-->
                    <!--                        }"-->
                    <!--                        class="hover:underline"-->
                    <!--                    >-->
                    <!--                    </RouterLink>-->
                    {{ data.email }}
                </template>
            </Column>
            <Column field="status" header="Status" :sortable="true">
                <template #body="{data}">
                    <Tag :severity="data.status === 'pending' ? 'warn' : 'danger'">
                        <span class="text-xs">{{ data.status }}</span>
                    </Tag>
                </template>
            </Column>
            <Column field="to" header="To" :sortable="true">
                <template #body="{data}">
                    <span class="text-xs">
                        {{ data.for.name ?? `${data.for.first_name} ${data.for.last_name}` }}
                    </span>
                </template>
            </Column>
            <Column field="by" header="By" :sortable="true">
                <template #body="{data}">
                    <span class="text-xs">
                        {{ data.user?.name }}
                    </span>
                </template>
            </Column>
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : null }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <TableRecordActions
                        name="Invitation"
                        :deleted="!!data.deleted_at"
                        :routes="{
                            delete: route('admin.invitations.destroy', data.id),
                        }"
                        @deleted="getInvitations"
                    >
                        <!-- accept button -->
                        <Button
                            v-tooltip.left="`Accept`"
                            v-if="data.status === 'pending'"
                            class="h-8 w-8 bg-green-600 text-white"
                            @click="acceptInvitation($event, data)"
                            :icon="{icon: 'mdi:check', width: 16}"
                        />
                    </TableRecordActions>
                </template>
            </Column>
            <template #expansion="{data}">
                <pre>{{ data }}</pre>
            </template>
        </DataTable>
    </div>
</template>
<style scoped></style>
